// HESHAURA — customer-facing policy & legal copy (display content).
// Plain, serializable content so pages map 1:1 to a future CMS/API response.
import { POLICIES } from "./policies";
import { formatINR } from "./content";

const UPDATED = "Last updated: June 2026";

export const LEGAL_DOCS = {
  "privacy-policy": {
    overline: "Legal",
    title: "Privacy Policy",
    intro:
      "This policy explains what information HESHAURA collects, how we use it, and the choices you have. We keep it simple and collect only what we need to serve you well.",
    updated: UPDATED,
    sections: [
      {
        heading: "Information we collect",
        body: [
          "When you place an order, create an account or join our WhatsApp list, we collect details such as your name, WhatsApp number, email, shipping address and order history.",
          "We also collect basic technical information (such as device and browsing activity on our store) to keep the experience fast, secure and relevant.",
        ],
      },
      {
        heading: "How we use your information",
        body: [
          "To process and deliver your orders, confirm payments, and provide customer support.",
          "To send order updates and — only with your consent — occasional news about small-batch drops and offers on WhatsApp. You can opt out at any time by replying STOP.",
        ],
      },
      {
        heading: "Sharing",
        body: [
          "We do not sell your personal information. We share it only with trusted partners who help us run the store — such as payment, shipping and messaging providers — and only to the extent needed to fulfil your order.",
        ],
      },
      {
        heading: "Your choices",
        body: [
          "You may request access to, correction of, or deletion of your personal data, and you can unsubscribe from marketing messages at any time.",
          "For any privacy request, contact us through the Contact page and we will respond within a reasonable time.",
        ],
      },
    ],
  },

  terms: {
    overline: "Legal",
    title: "Terms of Service",
    intro:
      "These terms govern your use of the HESHAURA store and the purchase of our handcrafted bracelets. By placing an order, you agree to these terms.",
    updated: UPDATED,
    sections: [
      {
        heading: "Products & pricing",
        body: [
          "Each bracelet is hand-finished in small batches, so slight, natural variations in stones, tone and finish are part of their charm and are not defects.",
          "All prices are listed in Indian Rupees (INR) and include applicable taxes unless stated otherwise. We may update prices and product availability at any time.",
        ],
      },
      {
        heading: "Orders",
        body: [
          `Orders are subject to acceptance and stock availability. A minimum order value of ${formatINR(POLICIES.minOrderValue)} may apply at checkout.`,
          "We reserve the right to cancel any order in case of pricing errors, suspected fraud or stock issues, in which case any amount paid is refunded.",
        ],
      },
      {
        heading: "Offers & credits",
        body: [
          `Promotional benefits — such as the ${POLICIES.firstOrderDiscount}% first-order discount and referral rewards — are indicative and are calculated and verified at checkout. Offers cannot be exchanged for cash.`,
        ],
      },
      {
        heading: "Intellectual property",
        body: [
          "All content on this store — including the HESHAURA name, logo, designs, photography and text — belongs to HESHAURA and may not be used without written permission.",
        ],
      },
    ],
  },

  "shipping-policy": {
    overline: "Care",
    title: "Shipping & Delivery",
    intro:
      "How and when your HESHAURA bracelets reach you. We ship across India, carefully packed in our signature gift-ready box.",
    updated: UPDATED,
    sections: [
      {
        heading: "Processing time",
        body: [
          "Because every piece is hand-finished in small batches, orders are typically processed and dispatched within 2–4 business days.",
        ],
      },
      {
        heading: "Delivery time & charges",
        body: [
          "Once dispatched, orders usually arrive within 4–8 business days depending on your location.",
          "We offer complimentary shipping across India. Any applicable charges, if introduced, are shown clearly at checkout.",
        ],
      },
      {
        heading: "Tracking",
        body: [
          "You will receive dispatch and tracking updates on your WhatsApp number and email so you always know where your order is.",
        ],
      },
    ],
  },

  "returns-refunds": {
    overline: "Care",
    title: "Returns & Refunds",
    intro:
      "We want you to love your bracelet. If something isn't right, here's how returns and refunds work.",
    updated: UPDATED,
    sections: [
      {
        heading: "Eligibility",
        body: [
          "You may request a return within 7 days of delivery, provided the bracelet is unused, in its original condition and packaging, with all tags intact.",
          "For hygiene and safety reasons, certain personalised or made-to-order pieces may not be eligible for return.",
        ],
      },
      {
        heading: "Damaged or incorrect items",
        body: [
          "If your order arrives damaged or you received the wrong item, contact us within 48 hours of delivery with photos and we will arrange a replacement or refund at no cost to you.",
        ],
      },
      {
        heading: "Refunds",
        body: [
          "Once your returned item is received and inspected, approved refunds are processed to your original payment method within 5–7 business days.",
          "Shipping charges, if any, are non-refundable except where the return is due to our error.",
        ],
      },
    ],
  },

  "cancellation-policy": {
    overline: "Care",
    title: "Cancellation Policy",
    intro:
      "Changed your mind? Here's how order cancellations work at HESHAURA.",
    updated: UPDATED,
    sections: [
      {
        heading: "Cancelling an order",
        body: [
          "You can request a cancellation before your order is dispatched. Once a small-batch piece has been dispatched, it can no longer be cancelled but may be eligible for return under our Returns & Refunds policy.",
        ],
      },
      {
        heading: "How to cancel",
        body: [
          "To cancel, reach out through the Contact page or reply to your order confirmation on WhatsApp with your order number as soon as possible.",
        ],
      },
      {
        heading: "Refund on cancellation",
        body: [
          "If you cancel before dispatch and have already paid, the full amount is refunded to your original payment method within 5–7 business days.",
        ],
      },
    ],
  },
};
